import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, ArrowRight, Briefcase, Users, Gift } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'client',
  });

  // Get referral code from URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      // Auto-select professional role if coming from referral link
      setFormData(prev => ({ ...prev, role: 'professional' }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleSelect = (role) => {
    setFormData({ ...formData, role });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error(language === 'fr' ? 'Le mot de passe doit contenir au moins 6 caractères' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      // Add referral code if present
      if (referralCode) {
        registerData.referral_code = referralCode;
      }
      const user = await register(registerData);
      toast.success(language === 'fr' ? 'Inscription réussie !' : 'Registration successful!');
      
      if (user.role === 'professional') {
        navigate('/pro/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl text-slate-900 mb-2" style={{ fontFamily: '"DM Serif Display", serif' }}>
            {t('createAccount')}
          </h1>
          <p className="text-slate-600">
            {t('joinHandyverse')}
          </p>
        </div>

        <Card className="border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_12px_24px_rgba(0,0,0,0.04)]">
          <CardContent className="pt-6">
            {/* Referral Banner */}
            {referralCode && (
              <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <Gift className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">
                      {language === 'fr' ? 'Lien de parrainage détecté!' : 'Referral link detected!'}
                    </p>
                    <p className="text-sm text-green-700">
                      {language === 'fr' 
                        ? 'Vous et votre parrain bénéficierez d\'un mois gratuit!'
                        : 'You and your referrer will get a free month!'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Role Selection */}
              <div className="space-y-2">
                <Label className="text-slate-700">{t('iAm')}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleRoleSelect('client')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      formData.role === 'client'
                        ? 'border-orange-600 bg-orange-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="role-client-btn"
                  >
                    <Users className={`h-6 w-6 ${formData.role === 'client' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <span className={`font-medium ${formData.role === 'client' ? 'text-orange-600' : 'text-slate-600'}`}>
                      {t('individual')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRoleSelect('professional')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      formData.role === 'professional'
                        ? 'border-orange-600 bg-orange-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="role-professional-btn"
                  >
                    <Briefcase className={`h-6 w-6 ${formData.role === 'professional' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <span className={`font-medium ${formData.role === 'professional' ? 'text-orange-600' : 'text-slate-600'}`}>
                      {t('professional')}
                    </span>
                  </button>
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-slate-700">{t('firstName')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      placeholder="Jean"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                      required
                      data-testid="register-firstname-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-slate-700">{t('lastName')}</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    placeholder="Tremblay"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    required
                    data-testid="register-lastname-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="votre@email.ca"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700">{t('phone')}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="819 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">{t('password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700">{t('confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-semibold btn-press"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('createAccount')}...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {t('createMyAccount')}
                    <ArrowRight className="h-5 w-5" />
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-slate-600">
          {t('alreadyAccount')}{' '}
          <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">
            {t('login')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
